import { AxiosResponse } from "axios";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialAdminLeadState } from "store/constants";
import {
  apiFail,
  apiSuccess,
  endPoints,
  leadTypes,
  selectAll,
} from "utils/constants";
import {
  dataManipulationForAdminLead,
  getErrorMessage,
  getPaginatedUrlWithSearchParams,
} from "store/helper";
import { RootState } from "store";
import { addSource } from "./sourceSlice";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { leadEndPoint } = endPoints;
const { rawLead, polishedLead } = leadTypes;

export const fetchAllAdminLeads = createAsyncThunk(
  "leads/fetchAllAdminLeads",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: leadEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAdminLeadsBulk = createAsyncThunk(
  "leads/deleteAdminLeadsBulk",
  async ({ leadIds }: { leadIds: string[] }, { rejectWithValue }) => {
    try {
      const deletePromises = leadIds.map((id) =>
        crudService.delete(`${leadEndPoint}`, id)
      );

      const responses = await Promise.all(deletePromises);

      if (responses.every((res: any) => res.status === 204)) {
        NotificationManager.success("Leads Deleted Successfully.");

        return responses.map((res: any) => res.data);
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addAdminLeadsBulk = createAsyncThunk(
  "leads/addAdminLeadsBulk",
  async (
    {
      leads: newLeads,
      onProgress,
    }: { leads: any; onProgress?: (progress: number) => void },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const completeResponse: any[] = [];

      const state = getState() as RootState;
      const sourceArr = state?.dropdownLeadReducer?.data?.source?.objects?.map(
        (item: any) => item?.source
      );

      for (const [index, lead] of newLeads.entries()) {
        try {
          if (lead?.source && !sourceArr?.includes(lead?.source)) {
            await dispatch(
              addSource({
                source: lead?.source,
                description: lead?.description,
                createdby_user: lead?.createdby_user,
                updatedby_user: lead?.updatedby_user,
              })
            );
          }

          const response: any = await crudService.create(
            `${leadEndPoint}/`,
            lead
          );

          if (response.status === 201) {
            completeResponse.push({
              result: apiSuccess,
              type: response?.data?.is_polish_lead ? polishedLead : rawLead,
              rowNumber: index + 1,
            });
          } else {
            completeResponse.push({ result: apiFail, rowNumber: index + 1 });
          }
        } catch (error) {
          const errorMessage = getErrorMessage(error);

          NotificationManager.error(errorMessage);
          // return rejectWithValue(error.message);

          completeResponse.push({
            result: apiFail,
            rowNumber: index + 1,
            lead: lead?.phone_number,
            reason: getErrorMessage(error),
          });
        }

        if (onProgress) {
          const percent: number = Math.round(
            ((index + 1) / newLeads.length) * 100
          );
          onProgress(percent);
        }
      }

      // const createPromises = newLeads.map(async (lead: any, index: number) => {
      //   try {
      //     if (lead?.source && !sourceArr?.includes(lead?.source)) {
      //       await dispatch(
      //         addSource({
      //           source: lead?.source,
      //           description: lead?.description,
      //           createdby_user: lead?.createdby_user,
      //           updatedby_user: lead?.updatedby_user,
      //         })
      //       );
      //     }
      //     const response: any = await crudService.create(
      //       `${leadEndPoint}/`,
      //       lead
      //     );
      //     if (response.status === 201) {
      //       return {
      //         result: apiSuccess,
      //         type: response?.data?.is_polish_lead ? polishedLead : rawLead,
      //         rowNumber: index + 1,
      //       };
      //     } else {
      //       return { result: apiFail, rowNumber: index + 1 };
      //     }
      //   } catch (error) {
      //     const errorMessage = getErrorMessage(error);

      //     NotificationManager.error(errorMessage);
      //     // return rejectWithValue(error.message);
      //     return {
      //       result: apiFail,
      //       rowNumber: index + 1,
      //       lead: lead?.phone_number,
      //       reason: getErrorMessage(error),
      //     };
      //   }
      // });
      // const responses = await Promise.all(createPromises);
      if (completeResponse.every((res: any) => res.status === 201)) {
        NotificationManager.success("Leads created Successfully.");
      }
      return completeResponse;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateAdminLeadsBulk = createAsyncThunk(
  "leads/updateAdminLeadsBulk",
  async (
    { updatedData, variant }: { updatedData: any; variant?: string },
    { rejectWithValue }
  ) => {
    try {
      const updatePromises = updatedData.map((leadData: any) =>
        crudService.update(`${leadEndPoint}`, leadData?.lead_id, leadData)
      );

      const responses = await Promise.all(updatePromises);

      if (
        responses.every((res: any) => res.status === 204 || res.status === 200)
      ) {
        const msg =
          variant === "assign-lead"
            ? "Lead assigned successfully."
            : "Leads updated Successfully.";
        NotificationManager.success(msg);

        return responses.map((res: any) => res.data);
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const approveAdminLead = createAsyncThunk(
  "leads/approveAdminLead",
  async ({ updatedLead, leadId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${leadEndPoint}`,
        leadId,
        updatedLead
      );
      if (response.status === 204) {
        NotificationManager.success("Lead Shared Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const adminleadSlice = createSlice({
  name: "leads",
  initialState: initialAdminLeadState,
  reducers: {
    updateLeadSelect: (state: any, action: PayloadAction<any>) => {
      state.leads = state.leads.map((lead: any) => {
        if (lead?.lead_id === action.payload) {
          return { ...lead, select: !lead?.select };
        }

        return lead;
      });
    },

    updateAllLeadSelect: (state: any, action: PayloadAction<any>) => {
      state.leads = state.leads.map((lead: any) => {
        if (action.payload === selectAll) {
          return { ...lead, select: true };
        }

        return { ...lead, select: false };
      });
    },
  },
  extraReducers: (builder) => {
    // fetch all  leads
    builder
      .addCase(fetchAllAdminLeads.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllAdminLeads.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.leads = dataManipulationForAdminLead(action.payload?.objects);
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllAdminLeads.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.leads = [];
          state.meta = defaultMetaObj;
        }
      );

    // delete admin Leads
    builder
      .addCase(deleteAdminLeadsBulk.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteAdminLeadsBulk.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        deleteAdminLeadsBulk.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // update admin Lead

    builder
      .addCase(updateAdminLeadsBulk.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        updateAdminLeadsBulk.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateAdminLeadsBulk.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // update admin Lead

    // approve admin Lead

    builder
      .addCase(approveAdminLead.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        approveAdminLead.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        approveAdminLead.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // approve admin Lead

    // add admin Lead
    builder
      .addCase(addAdminLeadsBulk.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addAdminLeadsBulk.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addAdminLeadsBulk.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const adminleadReducer = adminleadSlice.reducer;
export const { updateLeadSelect, updateAllLeadSelect } = adminleadSlice.actions;
